/**
 * @fileoverview Trial-focused panel for sales and lifecycle management of department trial subscriptions.
 * @module system-web/pages/TrialPanel
 *
 * Responsibilities:
 * - Render trial-specific department context, extension history, and lifecycle actions.
 * - Delegate extension, activation, and cleanup actions to parent service-backed handlers.
 *
 * Dependencies:
 * - systemAuditLogService
 * - ui-admin audit timeline components
 *
 * Architecture:
 * - Local-first. The panel renders tenant state supplied by page containers and never issues tenant lifecycle API calls directly.
 *
 * Notes:
 * - Sales users should be able to extend trials and activate departments from this surface.
 */
import React, { useState } from 'react';
import { Clock, AlertTriangle, Sparkles, ArrowRightCircle, Trash2, Building2, Mail, Phone, Tag, Calendar, RefreshCw } from 'lucide-react';
import { useTheme } from '@ui-admin/contexts/ThemeContext';
import AuditLogTimeline from '@ui-admin/components/AuditLogTimeline';
import { fetchAuditLogs } from '../services/systemAuditLogService';

/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - The panel renders local trial state and delegates extension and activation actions to parent service-backed handlers.
 *
 * Sync Strategy:
 * - Parent pages reconcile authoritative lifecycle route results back into Dexie-backed tenant state.
 *
 * Conflict Resolution:
 * - Parent service reconciliation determines the final panel state after an optimistic action.
 */

/**
 * @errorHandling
 *
 * Strategy:
 * - Lifecycle failures are surfaced by parent containers while the panel remains available for follow-up actions.
 *
 * User Impact:
 * - Sales users can stay in the trial panel and retry actions without losing context.
 *
 * Logging:
 * - REQUIRED
 */

// ============================================================
// Types
// ============================================================

/**
 * @contract TrialPanelTab
 *
 * Purpose:
 * - Defines the supported trial panel tabs.
 *
 * Fields:
 * - overview: Department summary.
 * - trial: Trial dates, history, and metadata.
 * - actions: Lifecycle action shortcuts.
 * - audit: Audit timeline.
 *
 * Rules:
 * - Parent containers own the actual tenant state mutations.
 *
 * Sync:
 * - UI-only contract backed by local-first tenant state.
 */
export type TrialPanelTab = 'overview' | 'trial' | 'actions' | 'audit';

/**
 * @contract TrialPanelProps
 *
 * Purpose:
 * - Defines the tenant data and handlers needed to render the trial lifecycle panel.
 *
 * Fields:
 * - tenant: Trial department record.
 * - onExtend: Shared trial extension handler.
 * - onConvert: Shared activation handler.
 * - onCleanup: Shared cleanup handler.
 *
 * Rules:
 * - Handlers must be backed by page-level service orchestration.
 *
 * Sync:
 * - Props reflect local-first state provided by the parent page.
 */
export interface TrialPanelProps {
  tenant: any;
  onExtend: (id: string, name: string) => void;
  onConvert: (id: string, name: string) => void;
  onCleanup: (id: string, name: string) => void;
}

// ============================================================
// Helpers
// ============================================================

const TIER_COLORS: Record<string, string> = {
  professional: 'bg-blue-100 text-blue-700',
  advanced: 'bg-purple-100 text-purple-700',
  premium: 'bg-amber-100 text-amber-700',
};

function ReadField({ label, value, mono, textPrimary, textSecondary }: {
  label: string;
  value?: any;
  mono?: boolean;
  textPrimary?: string;
  textSecondary?: string;
}) {
  const display = value === null || value === undefined || value === '' ? '—' : String(value);
  return (
    <div>
      <dt className="text-xs font-medium mb-0.5" style={{ color: textSecondary }}>{label}</dt>
      <dd className={`text-sm break-all ${mono ? 'font-mono text-xs' : ''}`} style={{ color: textPrimary }}>{display}</dd>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, textSecondary }: { icon: any; title: string; textSecondary?: string }) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <Icon className="h-3.5 w-3.5" style={{ color: textSecondary }} />
      <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: textSecondary }}>{title}</h4>
    </div>
  );
}

// ============================================================
// TrialPanel
// ============================================================

/**
 * @component TrialPanel
 *
 * Purpose:
 * - Give sales and admins a focused panel for managing a department trial lifecycle.
 *
 * Props:
 * - tenant: Trial department.
 * - onExtend: Trial extension callback.
 * - onConvert: Activation callback.
 * - onCleanup: Cleanup callback.
 *
 * Behavior:
 * - Displays trial dates, extensions, and trial-specific actions in a compact panel.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Must delegate actions to parent handlers instead of mutating tenant lifecycle state directly.
 *
 * Performance:
 * - Limits rendering to the active tab to keep the side panel lightweight.
 */
export default function TrialPanel({ tenant, onExtend, onConvert, onCleanup }: TrialPanelProps) {
  const { theme: t } = useTheme();
  const [activeTab, setActiveTab] = useState<TrialPanelTab>('overview');

  const trial = tenant.trial ?? {};
  const isExpired = tenant._trialExpired ?? false;
  const daysLeft = tenant._trialDaysRemaining ?? 0;
  const isConverted = trial.outcome === 'converted';
  
  console.log('[TrialPanel] Rendering with:', {
    tenantId: tenant._id,
    tenantName: tenant.name,
    trialOutcome: trial.outcome,
    isConverted: isConverted,
    hasTrialObject: !!tenant.trial
  });

  const tabs: { id: TrialPanelTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'trial', label: 'Trial' },
    { id: 'actions', label: 'Actions' },
    { id: 'audit', label: 'Audit Log' },
  ];

  // ── Overview Tab ──

  const renderOverview = () => (
    <div className="space-y-4 p-4">
      {/* Identity */}
      <div>
        <SectionHeader icon={Building2} title="Department" textSecondary={t.textSecondary} />
        <div className="rounded-lg p-3 space-y-3" style={{ background: t.expandedBg }}>
          <div className="flex items-center gap-2 flex-wrap">
            {tenant.tier && (
              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${TIER_COLORS[tenant.tier] || 'bg-gray-100 text-gray-600'}`}>
                {tenant.tier}
              </span>
            )}
            {isConverted
              ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full"><Sparkles className="h-3 w-3" /> Converted</span>
              : isExpired
                ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full"><AlertTriangle className="h-3 w-3" /> Expired</span>
                : daysLeft <= 7
                  ? <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">{daysLeft}d left</span>
                  : <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-600 rounded-full">{daysLeft}d left</span>
            }
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <ReadField label="Department Type" value={tenant.departmentType} textPrimary={t.textPrimary} textSecondary={t.textSecondary} />
            <ReadField label="NERIS ID" value={tenant.nerisId} mono textPrimary={t.textPrimary} textSecondary={t.textSecondary} />
          </dl>
        </div>
      </div>

      {/* Contact */}
      <div>
        <SectionHeader icon={Mail} title="Contact" textSecondary={t.textSecondary} />
        <div className="rounded-lg p-3 space-y-3" style={{ background: t.expandedBg }}>
          <dl className="grid grid-cols-1 gap-y-3">
            <ReadField label="Contact Email" value={tenant.contactEmail} textPrimary={t.textPrimary} textSecondary={t.textSecondary} />
            <ReadField label="Contact Phone" value={tenant.contactPhone} textPrimary={t.textPrimary} textSecondary={t.textSecondary} />
          </dl>
        </div>
      </div>

      {/* Address */}
      {tenant.address && (
        <div>
          <SectionHeader icon={Tag} title="Address" textSecondary={t.textSecondary} />
          <div className="rounded-lg p-3" style={{ background: t.expandedBg }}>
            <p className="text-sm" style={{ color: t.textPrimary }}>
              {[tenant.address.street, tenant.address.city, tenant.address.state, tenant.address.zip].filter(Boolean).join(', ') || '—'}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  // ── Trial Tab ──

  const renderTrial = () => {
    const extensions: any[] = trial.extensions ?? [];
    return (
      <div className="space-y-4 p-4">
        {/* Trial dates */}
        <div>
          <SectionHeader icon={Calendar} title="Trial Period" textSecondary={t.textSecondary} />
          <div className="rounded-lg p-3 space-y-3" style={{ background: t.expandedBg }}>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <ReadField
                label="Start Date"
                value={trial.startDate ? new Date(trial.startDate).toLocaleDateString() : undefined}
                textPrimary={t.textPrimary} textSecondary={t.textSecondary}
              />
              <ReadField
                label="End Date"
                value={trial.endDate ? new Date(trial.endDate).toLocaleDateString() : undefined}
                textPrimary={t.textPrimary} textSecondary={t.textSecondary}
              />
              <ReadField
                label="Duration"
                value={trial.durationDays ? `${trial.durationDays} days` : undefined}
                textPrimary={t.textPrimary} textSecondary={t.textSecondary}
              />
              <ReadField
                label="Outcome"
                value={isConverted ? 'Converted' : trial.outcome || 'Pending'}
                textPrimary={t.textPrimary} textSecondary={t.textSecondary}
              />
            </dl>
          </div>
        </div>

        {/* Extensions */}
        <div>
          <SectionHeader icon={RefreshCw} title={`Extensions (${extensions.length})`} textSecondary={t.textSecondary} />
          <div className="rounded-lg p-3" style={{ background: t.expandedBg }}>
            {extensions.length === 0 ? (
              <p className="text-xs" style={{ color: t.textSecondary }}>No extensions have been applied.</p>
            ) : (
              <ul className="space-y-2">
                {extensions.map((ext: any, i: number) => (
                  <li key={i} className="border-b last:border-0 pb-2 last:pb-0" style={{ borderColor: t.accent + '20' }}>
                    <p className="text-sm font-medium" style={{ color: t.textPrimary }}>+{ext.days} days</p>
                    {ext.reason && <p className="text-xs mt-0.5" style={{ color: t.textSecondary }}>"{ext.reason}"</p>}
                    {ext.addedAt && (
                      <p className="text-xs mt-0.5" style={{ color: t.textSecondary }}>
                        {new Date(ext.addedAt).toLocaleDateString()}
                        {ext.addedBy ? ` · ${ext.addedBy}` : ''}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Support contact */}
        {trial.departmentContact?.name && (
          <div>
            <SectionHeader icon={Phone} title="Trial Contact" textSecondary={t.textSecondary} />
            <div className="rounded-lg p-3 space-y-3" style={{ background: t.expandedBg }}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <ReadField label="Name" value={trial.departmentContact.name} textPrimary={t.textPrimary} textSecondary={t.textSecondary} />
                <ReadField label="Title" value={trial.departmentContact.title} textPrimary={t.textPrimary} textSecondary={t.textSecondary} />
                <ReadField label="Email" value={trial.departmentContact.email} textPrimary={t.textPrimary} textSecondary={t.textSecondary} />
                <ReadField label="Phone" value={trial.departmentContact.phone} textPrimary={t.textPrimary} textSecondary={t.textSecondary} />
              </dl>
            </div>
          </div>
        )}

        {/* Sales notes */}
        {trial.salesNotes && (
          <div>
            <SectionHeader icon={Tag} title="Sales Notes" textSecondary={t.textSecondary} />
            <div className="rounded-lg p-3" style={{ background: t.expandedBg }}>
              <p className="text-sm" style={{ color: t.textPrimary }}>{trial.salesNotes}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Actions Tab ──

  const renderActions = () => (
    <div className="p-4 space-y-3">
      <p className="text-xs mb-4" style={{ color: t.textSecondary }}>
        Manage the trial lifecycle for <strong style={{ color: t.textPrimary }}>{tenant.name}</strong>.
      </p>

      {/* Extend */}
      {!isConverted && (
        <button
          type="button"
          onClick={() => onExtend(tenant._id, tenant.name)}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-sm font-medium transition-colors hover:bg-blue-50"
          style={{ borderColor: t.accent + '40', color: t.textPrimary }}
        >
          <Clock className="h-4 w-4 text-blue-500 shrink-0" />
          <div className="text-left">
            <p className="font-medium">Extend Trial</p>
            <p className="text-xs font-normal" style={{ color: t.textSecondary }}>Add more days to the current trial period</p>
          </div>
        </button>
      )}

      {/* Convert */}
      {!isConverted && (
        <button
          type="button"
          onClick={() => onConvert(tenant._id, tenant.name)}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-sm font-medium transition-colors hover:bg-green-50"
          style={{ borderColor: t.accent + '40', color: t.textPrimary }}
        >
          <ArrowRightCircle className="h-4 w-4 text-green-600 shrink-0" />
          <div className="text-left">
            <p className="font-medium">Activate Department</p>
            <p className="text-xs font-normal" style={{ color: t.textSecondary }}>Take this department off trial and move it into an active paying subscription</p>
          </div>
        </button>
      )}

      {/* Already converted */}
      {isConverted && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-green-50 border border-green-200">
          <Sparkles className="h-4 w-4 text-green-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-green-800">Already Converted</p>
            <p className="text-xs text-green-600">This trial has been converted to an active subscription.</p>
          </div>
        </div>
      )}

      {/* Clean up */}
      {isExpired && !isConverted && (
        <button
          type="button"
          onClick={() => onCleanup(tenant._id, tenant.name)}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-red-200 text-sm font-medium transition-colors hover:bg-red-50"
          style={{ color: t.textPrimary }}
        >
          <Trash2 className="h-4 w-4 text-red-500 shrink-0" />
          <div className="text-left">
            <p className="font-medium text-red-700">Clean Up Expired Trial</p>
            <p className="text-xs font-normal text-red-500">Suspend this tenant and archive its trial data</p>
          </div>
        </button>
      )}
    </div>
  );

  // ── Render ──

  return (
    <div className="flex flex-col h-full" style={{ color: t.textPrimary }}>
      {/* Tab bar */}
      <div className="flex gap-0 border-b shrink-0" style={{ borderColor: t.accent + '30' }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id ? 'border-orange-500' : 'border-transparent hover:border-gray-300'
            }`}
            style={{ color: activeTab === tab.id ? t.accent : t.textSecondary }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'trial' && renderTrial()}
        {activeTab === 'actions' && renderActions()}
        {activeTab === 'audit' && (
          <AuditLogTimeline
            documentId={tenant._id}
            collection="tenants"
            theme={t}
            active={activeTab === 'audit'}
            fetchFn={fetchAuditLogs}
          />
        )}
      </div>
    </div>
  );
}
